import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore, useCurrentRestaurantId, useCurrentRole } from '@/stores/authStore';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { Lock } from 'lucide-react';
import Logo from '@/components/Logo';
import { useCostAlertCount } from '@/components/CostAlertsBanner';
import { trackPartnerClick } from '@/lib/api';
import {
  LayoutDashboard,
  Package,
  ChefHat,
  UtensilsCrossed,
  ShoppingCart,
  Calculator,
  Truck,
  FileText,
  Settings,
  LogOut,
  Building2,
  ChevronDown,
  ChevronRight,
  Check,
  Palette,
  User,
  CreditCard,
  Shield,
  Clock,
  X,
  TrendingUp,
  BarChart3,
  Sparkles,
  FlaskConical,
  Scissors,
  HelpCircle,
  Warehouse,
  ScanLine,
  ClipboardCheck,
  Tag,
  ClipboardList,
  BrainCircuit,
  Receipt,
  ArrowLeftRight,
} from 'lucide-react';

interface NavSubItem {
  label: string;
  path: string;
  roles?: string[];
  mobileOnly?: boolean;
}

interface NavItem {
  icon: any;
  label: string;
  path?: string;
  roles?: string[];
  subitems?: NavSubItem[];
  badge?: string;
  requiredFeature?: string; // Feature key from plan_config.features
  requiredPlan?: string; // Plan badge to show when locked (e.g. 'PRO', 'SCALE')
  externalUrl?: string; // External URL that opens in a new tab
  partnerName?: string; // Partner name for click tracking
  superAdminOnly?: boolean; // Only visible to super admins
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: 'Análisis',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', roles: ['owner', 'admin', 'chef'] },
      { icon: BarChart3, label: 'Ventas & POS', path: '/sales', roles: ['owner', 'admin'], requiredFeature: 'pos_integrations', requiredPlan: 'PRO' },
      { icon: Receipt, label: 'P&L', path: '/pnl', roles: ['owner', 'admin'], superAdminOnly: true },
      { icon: FileText, label: 'Reportes', path: '/reports', roles: ['owner', 'admin'] },
    ],
  },
  {
    label: 'Recetas',
    items: [
      { icon: Palette, label: 'Cartas', path: '/cartas', roles: ['owner', 'admin', 'chef'] },
      { icon: ChefHat, label: 'Recetas', path: '/recipes', roles: ['owner', 'admin', 'chef'] },
      { icon: UtensilsCrossed, label: 'Menús', path: '/menus', roles: ['owner', 'admin', 'chef'] },
    ],
  },
  {
    label: 'Ingredientes',
    items: [
      { icon: Package, label: 'Ingredientes', path: '/ingredients', roles: ['owner', 'admin', 'chef'] },
      { icon: Scissors, label: 'Despieces', path: '/despieces', roles: ['owner', 'admin', 'chef'] },
      { icon: TrendingUp, label: 'Precios Mercado', path: '/ingredients/market-comparison', roles: ['owner', 'admin', 'chef'], requiredFeature: 'market_comparison', requiredPlan: 'SCALE' },
    ],
  },
  {
    label: 'Herramientas',
    items: [
      { icon: ScanLine, label: 'Escáner IA', path: '/importar', roles: ['owner', 'admin', 'chef'] },
      { icon: TrendingUp, label: 'Simulador', path: '/simulator', roles: ['owner', 'admin', 'chef'], requiredFeature: 'simulator', requiredPlan: 'PRO' },
      { icon: Sparkles, label: 'Optimizador', path: '/optimizer', roles: ['owner', 'admin', 'chef'], requiredFeature: 'ai_optimizer', requiredPlan: 'PRO' },
      { icon: FlaskConical, label: 'Laboratorio', path: '/lab', roles: ['owner', 'admin', 'chef'], requiredFeature: 'lab', requiredPlan: 'SCALE' },
    ],
  },
  {
    label: 'Compras',
    items: [
      { icon: ShoppingCart, label: 'Pedidos', path: '/orders', roles: ['owner', 'admin', 'chef'] },
      { icon: BrainCircuit, label: 'Lista Compra IA', path: '/compras', roles: ['owner', 'admin', 'chef'], requiredFeature: 'smart_purchase', requiredPlan: 'PRO' },
      { icon: Calculator, label: 'Planificador', path: '/purchase-planner', roles: ['owner', 'admin', 'chef'] },
      { icon: Truck, label: 'Proveedores', path: '/suppliers', roles: ['owner', 'admin', 'chef'] },
      { icon: Receipt, label: 'Albaranes', path: '/albaranes', roles: ['owner', 'admin', 'chef'] },
      { icon: ArrowLeftRight, label: 'Conciliación', path: '/conciliacion', roles: ['owner', 'admin'], requiredFeature: 'reconciliation', requiredPlan: 'PRO' },
      { icon: Warehouse, label: 'Almacén', path: '/almacen', roles: ['owner', 'admin', 'chef'], requiredFeature: 'warehouse', requiredPlan: 'PRO' },
    ],
  },
  {
    label: 'Trazabilidad',
    items: [
      { icon: ClipboardCheck, label: 'Trazabilidad', path: '/trazabilidad', roles: ['owner', 'admin', 'chef'], requiredFeature: 'traceability', requiredPlan: 'SCALE' },
      { icon: Tag, label: 'Etiquetas', path: '/etiquetas', roles: ['owner', 'admin', 'chef'], requiredFeature: 'traceability', requiredPlan: 'SCALE' },
      { icon: ClipboardList, label: 'Producción', path: '/produccion', roles: ['owner', 'admin', 'chef'], requiredFeature: 'traceability', requiredPlan: 'SCALE' },
    ],
  },
  {
    label: 'Equipo',
    items: [
      {
        icon: Clock,
        label: 'Control Horario',
        roles: ['owner', 'admin', 'chef', 'viewer', 'time_only'],
        requiredFeature: 'time_tracking',
        requiredPlan: 'PRO',
        subitems: [
          { label: 'Mi Fichaje', path: '/time-clock', roles: ['owner', 'admin', 'chef', 'viewer', 'time_only'] },
          { label: 'Gestión Empleados', path: '/time-tracking', roles: ['owner', 'admin'] },
          { label: 'Turnos', path: '/time-tracking?tab=turnos', roles: ['owner', 'admin'] },
        ],
      },
    ],
  },
];

const helpItem: NavItem = { icon: HelpCircle, label: 'Ayuda', path: '/ayuda' };
const settingsItem: NavItem = { icon: Settings, label: 'Configuración', path: '/settings', roles: ['owner', 'admin'] };

function NavMenuItem({
  item,
  currentRole,
  isSuperAdmin,
  onLinkClick,
  showDot,
}: {
  item: NavItem;
  currentRole: string | null;
  isSuperAdmin: boolean;
  onLinkClick: () => void;
  showDot?: boolean;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const status = useSubscriptionStore((s) => s.status);

  const Icon = item.icon;

  // Check if feature is locked by plan
  const isLocked = !isSuperAdmin &&
    item.requiredFeature &&
    status?.plan_config?.features &&
    !(status.plan_config.features as Record<string, boolean>)[item.requiredFeature] &&
    status.active_plan !== 'trial'; // Trial users get full access

  // Check if this item or any subitem is active
  const isActive = item.path
    ? location.pathname === item.path ||
      (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
    : item.subitems?.some(sub => location.pathname === sub.path) || false;

  // If locked, show locked state and redirect to upgrade
  if (isLocked) {
    return (
      <button
        onClick={() => { onLinkClick(); navigate('/upgrade'); }}
        className="w-full flex items-center gap-3 px-3.5 py-3 rounded-lg text-sm font-medium text-slate-500 hover:text-slate-300 hover:bg-slate-700/50 transition-colors"
      >
        <Icon className="w-5 h-5 opacity-50" />
        <span className="flex-1 text-left opacity-70">{item.label}</span>
        <span className="flex items-center gap-1 text-[10px] font-semibold bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">
          <Lock className="w-3 h-3" />
          {item.requiredPlan}
        </span>
      </button>
    );
  }

  // If item has an external URL, render as an external link
  if (item.externalUrl) {
    const handleExternalClick = () => {
      onLinkClick();
      trackPartnerClick(item.partnerName || item.label, item.externalUrl!);
    };
    return (
      <a
        href={item.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleExternalClick}
        className="flex items-center gap-3 px-3.5 py-3 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
      >
        <Icon className="w-5 h-5" />
        <span className="flex-1">{item.label}</span>
      </a>
    );
  }

  // If item has subitems, render as expandable menu
  if (item.subitems) {
    // Filter subitems by role
    const visibleSubitems = item.subitems.filter(subitem => {
      if (isSuperAdmin) return true;
      if (!subitem.roles) return true;
      if (!currentRole) return false;
      return subitem.roles.includes(currentRole);
    });

    if (visibleSubitems.length === 0) return null;

    return (
      <div>
        <button
          onClick={() => setIsSubmenuOpen(!isSubmenuOpen)}
          className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-lg text-sm font-medium transition-colors ${
            isActive
              ? 'bg-blue-500 text-white'
              : 'text-slate-400 hover:text-white hover:bg-slate-700'
          }`}
        >
          <Icon className="w-5 h-5" />
          <span className="flex-1 text-left">{item.label}</span>
          {isSubmenuOpen ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>

        {/* Animated submenu */}
        <div
          className="grid transition-[grid-template-rows] duration-200 ease-out"
          style={{ gridTemplateRows: isSubmenuOpen ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            <div className="ml-4 mt-1 space-y-1">
              {visibleSubitems.map((subitem) => {
                const isSubActive = location.pathname === subitem.path;
                return (
                  <Link
                    key={subitem.path}
                    to={subitem.path}
                    onClick={onLinkClick}
                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-colors ${
                      isSubActive
                        ? 'bg-blue-600 text-white font-medium'
                        : 'text-slate-400 hover:text-white hover:bg-slate-700'
                    } ${subitem.mobileOnly ? 'md:hidden' : ''}`}
                  >
                    <span>{subitem.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular menu item without subitems
  if (!item.path) return null;

  return (
    <Link
      to={item.path}
      onClick={onLinkClick}
      className={`flex items-center gap-3 px-3.5 py-3 rounded-lg text-sm font-medium transition-colors ${
        isActive
          ? 'bg-blue-500 text-white'
          : 'text-slate-400 hover:text-white hover:bg-slate-700'
      }`}
    >
      <Icon className="w-5 h-5" />
      <span className="flex-1">{item.label}</span>
      {showDot && (
        <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
      )}
      {item.requiredPlan && (
        <span className="text-[10px] font-semibold bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded">
          {item.requiredPlan}+
        </span>
      )}
    </Link>
  );
}

function RestaurantSelector() {
  const { user, restaurants, setCurrentRestaurant, checkAuth } = useAuthStore();
  const currentRestaurantId = useCurrentRestaurantId();
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCity, setNewCity] = useState('');
  const [creating, setCreating] = useState(false);

  const currentRestaurant = restaurants.find(r => r.id === currentRestaurantId);
  const hasMultipleRestaurants = restaurants.length > 1;
  const canCreate = user?.can_multi_restaurant && restaurants.length < (user?.max_restaurants ?? 1);

  if (restaurants.length === 0) {
    return null;
  }

  const handleCreate = async () => {
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      const { restaurantApi } = await import('@/lib/api');
      const res = await restaurantApi.create({ name: newName.trim(), city: newCity.trim() || undefined });
      // Refresh restaurants from server
      await checkAuth();
      // Switch to new restaurant
      setCurrentRestaurant(res.data.restaurant.id);
      setShowCreateModal(false);
      setNewName('');
      setNewCity('');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Error al crear restaurante';
      alert(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="px-4 mb-4 flex-shrink-0">
      <div className="relative">
        <button
          onClick={() => (hasMultipleRestaurants || canCreate) && setIsOpen(!isOpen)}
          disabled={!hasMultipleRestaurants && !canCreate}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
            hasMultipleRestaurants || canCreate
              ? 'bg-slate-700 hover:bg-slate-600 cursor-pointer'
              : 'bg-slate-700/50 cursor-default'
          }`}
        >
          <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-white text-sm font-medium truncate">
              {currentRestaurant?.name || 'Seleccionar'}
            </p>
            {currentRestaurant?.city && (
              <p className="text-slate-400 text-xs truncate">{currentRestaurant.city}</p>
            )}
          </div>
          {(hasMultipleRestaurants || canCreate) && (
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          )}
        </button>

        {/* Dropdown */}
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute left-0 right-0 top-full mt-1 bg-slate-700 rounded-lg shadow-lg border border-slate-600 py-1 z-20 max-h-60 overflow-y-auto animate-in fade-in-0 zoom-in-95 duration-150">
              {restaurants.map((restaurant) => (
                <button
                  key={restaurant.id}
                  onClick={() => {
                    setCurrentRestaurant(restaurant.id);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-600 transition-colors"
                >
                  <div className="w-6 h-6 bg-slate-500 rounded flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-3 h-3 text-white" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-white text-sm truncate">{restaurant.name}</p>
                    {restaurant.city && (
                      <p className="text-slate-400 text-xs truncate">{restaurant.city}</p>
                    )}
                  </div>
                  {restaurant.id === currentRestaurantId && (
                    <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  )}
                </button>
              ))}

              {/* Add restaurant button */}
              {canCreate && (
                <>
                  <div className="border-t border-slate-600 my-1" />
                  <button
                    onClick={() => { setIsOpen(false); setShowCreateModal(true); }}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-600 transition-colors text-blue-400"
                  >
                    <div className="w-6 h-6 bg-blue-500/20 rounded flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-400 text-sm font-bold">+</span>
                    </div>
                    <span className="text-sm">Añadir restaurante</span>
                    <span className="text-xs text-slate-500 ml-auto">{restaurants.length}/{user?.max_restaurants}</span>
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create Restaurant Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-900">Nuevo restaurante</h3>
              <p className="text-sm text-slate-500">{restaurants.length}/{user?.max_restaurants} restaurantes</p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Ej: La Bodega del Mar"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ciudad</label>
                <input
                  type="text"
                  value={newCity}
                  onChange={(e) => setNewCity(e.target.value)}
                  placeholder="Ej: Barcelona"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                />
              </div>
            </div>
            <div className="px-6 py-3 border-t border-slate-100 flex gap-3 justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={!newName.trim() || creating}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {creating ? 'Creando...' : 'Crear restaurante'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ isOpen = false, onClose }: SidebarProps) {
  const { logout, user } = useAuthStore();
  const currentRole = useCurrentRole();
  const currentRestaurantId = useCurrentRestaurantId();
  const costAlertCount = useCostAlertCount();

  // Super admins (platform owners) see everything
  const isSuperAdmin = user?.is_admin === true;

  // For non-restaurant-21, replace "Control Horario" with external Skello link
  const ESTOQUE_RESTAURANT_ID = 21;
  const transformedNavGroups = navGroups.map(group => {
    if (group.label !== 'Equipo') return group;
    return {
      ...group,
      items: group.items.map(item => {
        if (item.label !== 'Control Horario') return item;
        if (currentRestaurantId === ESTOQUE_RESTAURANT_ID) return item;
        // Replace with external Skello link for all other restaurants
        return {
          icon: item.icon,
          label: 'Control Horario',
          externalUrl: 'https://www.skello.es/?utm_source=pleko&utm_medium=app&utm_campaign=partner&utm_content=control-horario',
          partnerName: 'Skello',
          roles: item.roles,
        } as NavItem;
      }),
    };
  });

  // Filter items by role
  const filterByRole = (items: NavItem[]) => items.filter(item => {
    if (item.superAdminOnly && !isSuperAdmin) return false;
    if (isSuperAdmin) return true;
    if (!item.roles) return true;
    if (!currentRole) return false;
    return item.roles.includes(currentRole);
  });

  // Build filtered groups (hide empty groups)
  const filteredGroups = transformedNavGroups
    .map(group => ({ ...group, items: filterByRole(group.items) }))
    .filter(group => group.items.length > 0);

  // Settings + admin items (shown separately at bottom)
  const showSettings = isSuperAdmin || (currentRole && settingsItem.roles?.includes(currentRole));
  const adminItem: NavItem | null = isSuperAdmin ? { icon: Shield, label: 'Panel Admin', path: '/admin' } : null;

  const handleLinkClick = () => {
    // Close sidebar on mobile when a link is clicked
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        w-[260px] bg-slate-800 h-dvh max-h-dvh flex flex-col overflow-hidden
        fixed md:sticky top-0 z-50
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
      {/* Logo + Close Button (Mobile) */}
      <div className="p-6 pb-4 flex items-start justify-between flex-shrink-0">
        <div className="flex-1">
          <Logo linkTo="/dashboard" variant="light" />
          {user?.is_admin && (
            <div className="mt-2 px-1">
              <span className="text-xs font-semibold text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded">
                ADMIN
              </span>
            </div>
          )}
        </div>
        {/* Close button - only visible on mobile */}
        <button
          onClick={onClose}
          className="md:hidden text-slate-400 hover:text-white transition-colors p-1"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Restaurant Selector */}
      <RestaurantSelector />

      {/* Navigation */}
      <nav className="px-4 pb-4 flex-1 overflow-y-auto min-h-0 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent hover:scrollbar-thumb-slate-500" style={{ scrollbarWidth: 'thin', scrollbarColor: '#475569 transparent' }}>
        {filteredGroups.map((group) => (
          <div key={group.label} className="mb-1">
            <div className="px-3 pt-4 pb-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold select-none">
              {group.label}
            </div>
            <div className="space-y-0.5">
              {group.items.map((item, index) => (
                <NavMenuItem
                  key={item.path || `submenu-${index}`}
                  item={item}
                  currentRole={currentRole}
                  isSuperAdmin={isSuperAdmin}
                  onLinkClick={handleLinkClick}
                  showDot={item.path === '/dashboard' && costAlertCount > 0}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Help + Settings + Admin (bottom of nav) */}
        <div className="mt-2 pt-2 border-t border-slate-700">
          <NavMenuItem
            item={helpItem}
            currentRole={currentRole}
            isSuperAdmin={isSuperAdmin}
            onLinkClick={handleLinkClick}
          />
          {showSettings && (
            <NavMenuItem
              item={settingsItem}
              currentRole={currentRole}
              isSuperAdmin={isSuperAdmin}
              onLinkClick={handleLinkClick}
            />
          )}
          {adminItem && (
            <NavMenuItem
              item={adminItem}
              currentRole={currentRole}
              isSuperAdmin={isSuperAdmin}
              onLinkClick={handleLinkClick}
            />
          )}
        </div>
      </nav>

      {/* User Menu — fixed at bottom */}
      <div className="flex-shrink-0">
        <UserMenu user={user} onLogout={logout} />
      </div>
      </aside>
    </>
  );
}

function UserMenu({ user, onLogout }: { user: { name: string; email: string; is_admin?: boolean } | null; onLogout: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const currentRole = useCurrentRole();
  const isSuperAdmin = user?.is_admin === true;

  // Build menu items based on role
  const baseMenuItems = [
    { icon: User, label: 'Mi perfil', path: '/settings', tab: 'profile' },
  ];

  // Super admins see everything
  if (isSuperAdmin) {
    baseMenuItems.push({ icon: CreditCard, label: 'Suscripción', path: '/settings', tab: 'subscription' });
    baseMenuItems.push({ icon: Settings, label: 'Configuración', path: '/settings', tab: '' } as any);
    baseMenuItems.push({ icon: Shield, label: 'Panel Admin', path: '/admin', tab: '' } as any);
  } else {
    // Time-only users only see their profile - no additional menu items
    if (currentRole !== 'time_only') {
      // Regular users: filter by role
      // Only owners can see subscription
      if (currentRole === 'owner') {
        baseMenuItems.push({ icon: CreditCard, label: 'Suscripción', path: '/settings', tab: 'subscription' });
      }

      // Only owners and admins can see settings
      if (currentRole === 'owner' || currentRole === 'admin') {
        baseMenuItems.push({ icon: Settings, label: 'Configuración', path: '/settings', tab: '' } as any);
      }
    }
  }

  const menuItems = baseMenuItems;

  return (
    <div className="mt-auto p-3 pb-safe border-t border-slate-700 relative flex-shrink-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-slate-700 active:bg-slate-600 transition-colors min-h-[52px]"
      >
        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-white text-base font-medium">
            {user?.name?.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-white text-sm font-medium truncate">{user?.name}</p>
          <p className="text-slate-400 text-xs truncate">{user?.email}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-3 right-3 bottom-full mb-2 bg-slate-700 rounded-lg shadow-lg border border-slate-600 py-1 z-20 animate-in fade-in-0 zoom-in-95 duration-150">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.label}
                  onClick={() => {
                    navigate(item.path);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-slate-300 hover:text-white hover:bg-slate-600 active:bg-slate-500 transition-colors min-h-[44px]"
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{item.label}</span>
                </button>
              );
            })}
            <hr className="my-1 border-slate-600" />
            <button
              onClick={() => {
                onLogout();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:text-red-300 hover:bg-slate-600 active:bg-slate-500 transition-colors min-h-[44px]"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm font-medium">Cerrar sesión</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
